<template>
	<div class="attachmentlist" ref="attachmentListRef">
		<div class="grid">
			<Spinner v-show="loading" />
			<div class="col-12 attachmentlist__list-filters-col">
				<AttachmentListFilters
					:value="filters"
					:fromDateDefault="fromDateDefault"
					:toDateDefault="toDateDefault"
					:availableSubjects="availableSubjects"
					:translations="translations"
					@update="updateFilters"
					:ariaLabelChooseFromDate="dataProps.arialabelchoosefromdate"
					:ariaLabelChooseToDate="dataProps.arialabelchoosetodate"
					:ariaLabelChooseSubject="dataProps.arialabelchoosesubject"
				></AttachmentListFilters>
			</div>
			<div class="col-12">
				<div class="attachmentlist__list">
					<h2 class="attachmentlist__title header-extra-small">
						{{ translations.title }}
					</h2>
					<div v-if="listItems.length">
						<div class="grid-noGutter attachmentlist__header">
							<div class="col-8">
								<div class="grid">
									<div
										class="col-4 attachmentlist__column-header"
									>
										{{ translations.date }}
									</div>
									<div
										class="col-8 attachmentlist__column-header"
									>
										{{ translations.name }}
									</div>
								</div>
							</div>
							<div class="col-4">
								<div class="grid">
									<div
										class="col-12_smx-4 attachmentlist__column-header"
									>
										{{ translations.files }}
									</div>
								</div>
							</div>
						</div>
						<ul class="ul--nostyling">
							<AttachmentListItem
								v-for="(listitem, index) in listItems"
								:key="index + listitem.attachmentPageID"
								:data="listitem"
								:translations="translations"
							></AttachmentListItem>
						</ul>
					</div>
					<ul class="ul--nostyling" v-else>
						<li
							class="attachmentlist__item attachmentlist__item-noresults"
						>
							{{ translations.translationNoResults }}
						</li>
					</ul>
				</div>
			</div>
			<footer class="col-12 text--center">
				<Pagination
					:totalItems="totalItems"
					:pageSize="parseInt(dataProps.pagesize)"
					:currentPage="parseInt(pageIndex)"
					@changePageIndex="changePageIndex"
					:ariaLabelPaginationGotoPage="
						dataProps.arialabelpaginationgotopage
					"
					:ariaLabelPaginationNav="dataProps.arialabelpaginationnav"
					:ariaLabelPaginationNext="dataProps.arialabelpaginationnext"
					:ariaLabelPaginationPrev="dataProps.arialabelpaginationprev"
					:ariaLabelPaginationActivePage="
						dataProps.arialabelpaginationactivepage
					"
				></Pagination>
			</footer>
		</div>
	</div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import AttachmentListItem from "./attachmentListItem.vue";
import AttachmentListFilters from "./attachmentListFilters.vue";
import Pagination from "../pagination/pagination.vue";
import axios from "axios";
import Spinner from "../../../shared/script/vue/Spinner.vue";

const attachmentListRef = ref(null);

const apiurl = ref("");
const listItems = ref([]);
const listid = ref("");
const filters = ref({
	searchTerm: "",
	subjects: [],
	fromDate: "",
	toDate: "",
});
const pageIndex = ref(0);
const pageSize = ref(10);
const loading = ref(false);
const totalItems = ref(0);
const availableSubjects = ref([]);
const dataProps = ref({});

const fromDateDefault = utilities.formatDate(
	new Date(new Date().setMonth(new Date().getMonth() - 3))
);
const toDateDefault = utilities.formatDate(
	new Date(new Date().setMonth(new Date().getMonth() + 2))
);

filters.value.fromDate = fromDateDefault;
filters.value.toDate = toDateDefault;

onMounted(() => {
	dataProps.value = attachmentListRef.value?.parentElement?.dataset;
	pageSize.value =
		dataProps.value.pagesize > 0
			? dataProps.value.pagesize
			: pageSize.value;
	apiurl.value = dataProps.value.apiurl;
	listid.value = dataProps.value.contentid;
	checkPageIndex();
	fetchData(true, false);
	checkQueryParameters();
});

const fetchData = async (includeSubjects = false, setQueryString = true) => {
	loading.value = true;

	const paramsObject = {
		AttachmentListID: parseInt(dataProps.value.contentid),
		ReturnSubjects: includeSubjects,
		PageIndex: pageIndex.value,
		PageSize: pageSize.value,
		SearchTerm: filters.value.searchTerm,
	};

	if (filters.value.fromDate.length > 0) {
		const dateArr = filters.value.fromDate.split(".");
		const year = parseInt(dateArr[2]);
		const month = parseInt(dateArr[1]);
		const day = parseInt(dateArr[0]);
		paramsObject.FromDate = `${year}-${month}-${day}`;
	}

	if (filters.value.toDate.length > 0) {
		const dateArr = filters.value.toDate.split(".");
		const year = parseInt(dateArr[2]);
		const month = parseInt(dateArr[1]);
		const day = parseInt(dateArr[0]);
		paramsObject.toDate = `${year}-${month}-${day}`;
	}

	let paramsString = `?${new URLSearchParams(paramsObject).toString()}`;

	if (filters.value.subjects.length > 0) {
		paramsString += constructArrayQueryString(
			filters.value.subjects,
			"Subjects"
		);
	}

	try {
		let response = await axios.get(apiurl.value + paramsString);

		if (response.status == 200) {
			listItems.value = response.data.attachmentPages;
			totalItems.value = response.data.totalNumberOfAttachmentPages;

			if (
				response.data.subjectsAvailable &&
				response.data.subjectsAvailable.length
			) {
				availableSubjects.value = response.data.subjectsAvailable;
			}
		} else {
			listItems.value = [];
		}
		loading.value = false;
		if (setQueryString) {
			setQueryStringParameters();
		}
		return response;
	} catch (err) {
		console.warn(err);
		loading.value = false;
		return false;
	}
};

const constructArrayQueryString = (array, name) => {
	let arrayString = "";
	for (let i = 0; i < array.length; ++i) {
		arrayString += `&${name}=${encodeURI(array[i])}`;
	}
	return arrayString;
};

const setQueryStringParameters = () => {
	let activeFilters = {};
	let setqueryString = false;
	if (filters.value.searchTerm.length) {
		activeFilters.searchTerm = filters.value.searchTerm;
		setqueryString = true;
	}
	if (filters.value.fromDate.length) {
		activeFilters.fromDate = filters.value.fromDate;
		setqueryString = true;
	}
	if (filters.value.toDate.length) {
		activeFilters.toDate = filters.value.toDate;
		setqueryString = true;
	}
	if (filters.value.subjects.length) {
		activeFilters.subjects = filters.value.subjects;
		setqueryString = true;
	}
	if (setqueryString) {
		utilities.setQueryStringParameter(
			"filters",
			JSON.stringify(activeFilters)
		);
	} else {
		utilities.deleteQueryStringParameter("filters");
	}
};

const changePageIndex = (index) => {
	let indexPlusOne = index + 1;
	if (
		indexPlusOne >= 1 &&
		indexPlusOne <= Math.ceil(totalItems.value / dataProps.value.pagesize)
	) {
		pageIndex.value = index;
		if (pageIndex.value > 0) {
			utilities.setQueryStringParameter(
				"pageIndex",
				JSON.stringify(pageIndex.value)
			);
		} else {
			utilities.deleteQueryStringParameter("pageIndex");
		}
		fetchData();
	}
};

const updateFilters = (newFilters) => {
	filters.value = JSON.parse(JSON.stringify(newFilters));
	pageIndex.value = 0;
	utilities.setQueryStringParameter("filters", JSON.stringify(filters.value));
	fetchData();
};

const checkPageIndex = () => {
	const urlParams = new URLSearchParams(window.location.search);
	const pageIndexParam = urlParams.get("pageIndex");
	if (pageIndexParam) {
		pageIndex.value = parseInt(pageIndexParam, 10);
	}
};

const checkQueryParameters = () => {
	const urlParams = new URLSearchParams(window.location.search);
	let filtersParam = urlParams.get("filters");
	let reload = false;
	if (filtersParam) {
		filtersParam = JSON.parse(filtersParam);
		filters.value = Object.assign(filters.value, filtersParam);
		reload = true;
	}
	if (reload) {
		fetchData();
	}
};

const translations = computed(() => {
	return dataProps.value.translations
		? JSON.parse(dataProps.value.translations)
		: {};
});
</script>
